/**
 * Data-fetching hooks for Ops V2. All use current org from orgContext (X-Org-Id).
 */

import { useState, useEffect, useCallback } from "react";
import { subscribeToOrgContext, getCurrentOrgId } from "../../state/orgContext";
import { opsV2Request } from "./client";
import type {
  OpsEngagementOut,
  ProcessCaseOut,
  StepInstanceOut,
  ActivityInstanceOut,
  ActivityWithContextOut,
  AssigneeOut,
  CostLineV2Out,
  DashboardSummaryOut,
  OperationalDocumentOut,
  EventLogEntryOut,
  CompleteActivityRequest,
  ProductProcessRuleSetOut,
  ProductProcessRuleOut,
  ActivityHandlerOut,
  PublicationEventOut,
  ProcessTemplateOut,
} from "./types";

export type OpsV2HookState<T> = {
  data: T | null;
  loading: boolean;
  error: Error | null;
  refetch: () => void;
};

function useOrgId(): string | null {
  const [orgId, setOrgId] = useState<string | null>(getCurrentOrgId);
  useEffect(() => {
    setOrgId(getCurrentOrgId());
    return subscribeToOrgContext(() => setOrgId(getCurrentOrgId()));
  }, []);
  return orgId;
}

export function useOpsEngagements(params?: { status?: string; limit?: number }): OpsV2HookState<OpsEngagementOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<OpsEngagementOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (params?.status) query.set("status", params.status);
    if (params?.limit) query.set("limit", String(params.limit));
    const path = `/engagements${query.toString() ? `?${query.toString()}` : ""}`;
    opsV2Request<OpsEngagementOut[]>(path, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, params?.status, params?.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useOpsEngagement(engagementId: string | null): OpsV2HookState<OpsEngagementOut> {
  const orgId = useOrgId();
  const [data, setData] = useState<OpsEngagementOut | null>(null);
  const [loading, setLoading] = useState(!!engagementId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !engagementId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<OpsEngagementOut>(`/engagements/${engagementId}`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, engagementId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useProcessCasesByEngagement(engagementId: string | null): OpsV2HookState<ProcessCaseOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProcessCaseOut[] | null>(null);
  const [loading, setLoading] = useState(!!engagementId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !engagementId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ProcessCaseOut[]>(`/engagements/${engagementId}/process-cases`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, engagementId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useStepsByProcessCase(processCaseId: string | null): OpsV2HookState<StepInstanceOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<StepInstanceOut[] | null>(null);
  const [loading, setLoading] = useState(!!processCaseId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !processCaseId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<StepInstanceOut[]>(`/process-cases/${processCaseId}/steps`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, processCaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useActivities(params?: {
  mine?: boolean;
  assignee_id?: string;
  due_before?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): OpsV2HookState<ActivityWithContextOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ActivityWithContextOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (params?.mine) query.set("mine", "true");
    if (params?.assignee_id) query.set("assignee_id", params.assignee_id);
    if (params?.due_before) query.set("due_before", params.due_before);
    if (params?.status) query.set("status", params.status);
    if (params?.limit) query.set("limit", String(params.limit));
    if (params?.offset) query.set("offset", String(params.offset));
    const path = `/activities${query.toString() ? `?${query.toString()}` : ""}`;
    opsV2Request<ActivityWithContextOut[]>(path, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, params?.mine, params?.assignee_id, params?.due_before, params?.status, params?.limit, params?.offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useAssignees(): OpsV2HookState<AssigneeOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<AssigneeOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<AssigneeOut[]>("/assignees", { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useDashboardSummary(): OpsV2HookState<DashboardSummaryOut> {
  const orgId = useOrgId();
  const [data, setData] = useState<DashboardSummaryOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<DashboardSummaryOut>("/dashboard/summary", { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useCurrentRuleSet(productId: string | null): OpsV2HookState<ProductProcessRuleSetOut | null> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProductProcessRuleSetOut | null>(null);
  const [loading, setLoading] = useState(!!productId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !productId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ProductProcessRuleSetOut | null>(`/products/${productId}/current-rule-set`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useRuleSetRules(ruleSetId: string | null): OpsV2HookState<ProductProcessRuleOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProductProcessRuleOut[] | null>(null);
  const [loading, setLoading] = useState(!!ruleSetId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !ruleSetId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ProductProcessRuleOut[]>(`/rule-sets/${ruleSetId}/rules`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, ruleSetId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useRuleSet(ruleSetId: string | null): OpsV2HookState<ProductProcessRuleSetOut | null> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProductProcessRuleSetOut | null>(null);
  const [loading, setLoading] = useState(!!ruleSetId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !ruleSetId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ProductProcessRuleSetOut>(`/rule-sets/${ruleSetId}`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, ruleSetId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useRuleSets(productId: string | null | undefined): OpsV2HookState<ProductProcessRuleSetOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProductProcessRuleSetOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (productId) query.set("product_id", productId);
    const path = `/rule-sets${query.toString() ? `?${query.toString()}` : ""}`;
    opsV2Request<ProductProcessRuleSetOut[]>(path, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useProcessTemplates(): OpsV2HookState<ProcessTemplateOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ProcessTemplateOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ProcessTemplateOut[]>("/process-templates", { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useActivityHandlers(): OpsV2HookState<ActivityHandlerOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ActivityHandlerOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ActivityHandlerOut[]>("/activity-handlers", { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function usePublications(params?: { entity_type?: string; limit?: number }): OpsV2HookState<PublicationEventOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<PublicationEventOut[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    if (params?.entity_type) query.set("entity_type", params.entity_type);
    if (params?.limit) query.set("limit", String(params.limit));
    const path = `/publications${query.toString() ? `?${query.toString()}` : ""}`;
    opsV2Request<PublicationEventOut[]>(path, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, params?.entity_type, params?.limit]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useCostLines(engagementId: string | null): OpsV2HookState<CostLineV2Out[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<CostLineV2Out[] | null>(null);
  const [loading, setLoading] = useState(!!engagementId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !engagementId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<CostLineV2Out[]>(`/engagements/${engagementId}/cost-lines`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, engagementId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useActivitiesByProcessCase(processCaseId: string | null): OpsV2HookState<ActivityInstanceOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<ActivityInstanceOut[] | null>(null);
  const [loading, setLoading] = useState(!!processCaseId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !processCaseId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<ActivityInstanceOut[]>(`/process-cases/${processCaseId}/activities`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, processCaseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useOperationalDocument(engagementId: string | null): OpsV2HookState<OperationalDocumentOut> {
  const orgId = useOrgId();
  const [data, setData] = useState<OperationalDocumentOut | null>(null);
  const [loading, setLoading] = useState(!!engagementId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !engagementId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    opsV2Request<OperationalDocumentOut>(`/engagements/${engagementId}/operational-document`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, engagementId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useEventLog(params: {
  entityType: string;
  entityId: string | null;
  limit?: number;
  offset?: number;
}): OpsV2HookState<EventLogEntryOut[]> {
  const orgId = useOrgId();
  const [data, setData] = useState<EventLogEntryOut[] | null>(null);
  const [loading, setLoading] = useState(!!params.entityId);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(() => {
    if (!orgId || !params.entityId) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const query = new URLSearchParams();
    query.set("entity_type", params.entityType);
    query.set("entity_id", params.entityId);
    if (params.limit) query.set("limit", String(params.limit));
    if (params.offset) query.set("offset", String(params.offset));
    opsV2Request<EventLogEntryOut[]>(`/event-log?${query.toString()}`, { orgId })
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, [orgId, params.entityType, params.entityId, params.limit, params.offset]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useCompleteActivity(): (
  activityInstanceId: string,
  body: CompleteActivityRequest
) => Promise<ActivityInstanceOut> {
  const orgId = useOrgId();
  return useCallback(
    (activityInstanceId: string, body: CompleteActivityRequest) => {
      if (!orgId) return Promise.reject(new Error("Aucune organisation sélectionnée"));
      return opsV2Request<ActivityInstanceOut>(`/activity-instances/${activityInstanceId}/complete`, {
        method: "POST",
        body,
        orgId,
      });
    },
    [orgId]
  );
}
